import InformationButton from "../../../components/molecules/InformationButton"
import SmallBusinessToolTipDescription from "../../../components/molecules/SmallBusinessToolTipDescription"
import Optins from "../../../components/organisms/Optins/Optins"
import { TENANTS } from "../../../constants"
import { getCountryFromLanguage, missEnumCode } from "../../../core/service/BusinessLogicService"
import { isPdiRetailTenant, isStrangerTenant, loc } from "../../../core/utils"
import { getSessionVoyageurLang } from "../../../core/voyageurLanguage"
import CheckboxField from "../../../form/CheckboxField"
import Date from "../../../form/Date"
import NumberFieldPhone from "../../../form/NumberFieldPhone"
import PhoneField from "../../../form/PhoneField"
import RadiosField from "../../../form/RadiosField"
import Select from "../../../form/Select"
import TextField from "../../../form/TextField"
import { getCivilStatuses } from "../../ClientPage/service/ClientPageServices"
import { DATE_FORMAT } from "../constants/identificationPageConstants"
import SelectIdentificationClientPage from "./componentsByCountry/ITComponents/SelectIdentificationClientPage"
import clsx from "clsx"
import React, { useEffect } from "react"
import { Button, Col, Form, FormGroup, Row } from "react-bootstrap"
import * as FinalForm from "react-final-form"

export default function IdentificationPageForm({ tunnelData, onSubmit, values, optins, isLoadingOptins }) {
  const { identificationPageConfiguration, tenant, shopUrl } = tunnelData
  const { identity, coordinates } = identificationPageConfiguration
  const country = getCountryFromLanguage(tunnelData?.data?.order?.orderLanguageTag)
  const languageTag = tunnelData?.data?.order?.orderLanguageTag || ""
  const isMediaMarkt = tenant === TENANTS.MEDIAMARKT_NL || tenant === TENANTS.MEDIAMARKT_BE
  const selectedLanguageTag = getSessionVoyageurLang() || languageTag
  const useMediaMarktDutchLabels = isMediaMarkt && selectedLanguageTag.startsWith("nl")
  const isFirstNameBeforeLastName =
    identity?.includes("firstName") &&
    identity?.includes("name") &&
    identity.indexOf("firstName") < identity.indexOf("name")

  const firstNameField = (
    <TextField
      hidden={!identity?.includes("firstName") && !identity?.includes("leaderFirstName")}
      name="identity.firstName"
      label={identity?.includes("firstName") ? loc`First name` : loc`leader first name`}
      trimOnBlur={true}
    />
  )

  const { change } = FinalForm.useForm()

  useEffect(() => {
    if (!optins || optins.length === 0) return
    change(
      "optins",
      optins.map((x) => ({
        code: x.code,
        checked: false
      }))
    )
  }, [change, optins])

  const backToSimulationPage = () => {
    if (isPdiRetailTenant(tenant)) {
      tunnelData.step = "stepHome"
      window.localStorage.setItem(
        "tunnelData",
        JSON.stringify({
          ...tunnelData
        })
      )
      const base = tunnelData?.pdiRetailUrl ?? ""
      const oplQ = encodeURIComponent(tunnelData?.opl ?? "")
      const withTenant =
        tenant === TENANTS.MEDIAMARKT_NL || tenant === TENANTS.MEDIAMARKT_BE
          ? `&tenant=${encodeURIComponent(tenant)}`
          : ""
      window.location.href = `${base}simulation?opl=${oplQ}${withTenant}`
    }
  }

  const useSimulationBackInsteadOfShop =
    isPdiRetailTenant(tenant) && (tenant === TENANTS.MEDIAMARKT_NL || tenant === TENANTS.MEDIAMARKT_BE)

  return (
    <Form onSubmit={onSubmit}>
      <Row className="justify-content-center">
        <Col lg={6}>
          <div className="hard-shadow-white-block">
            <CheckboxField
              hidden={!identity?.includes("isSmallBusiness")}
              name="identity.isSmallBusiness"
              checked={values.identity?.isSmallBusiness}
              label={loc`SmallBusiness`}
            >
              <InformationButton
                overlayText={loc("SmallBusinessDescription", {
                  text: <SmallBusinessToolTipDescription />
                })}
              />
            </CheckboxField>
            <RadiosField
              hidden={!identity?.includes("title")}
              name="identity.title"
              checked={values.identity?.title}
              items={getCivilStatuses(tunnelData)}
              label={loc`Title`}
            />
            {isFirstNameBeforeLastName && firstNameField}
            <TextField
              hidden={!identity?.includes("name") && !identity?.includes("leaderName")}
              name="identity.name"
              label={
                identity?.includes("name")
                  ? loc(useMediaMarktDutchLabels ? "Family name" : "Last name")
                  : loc`leader name`
              }
              trimOnBlur={true}
            />
            {values.person?.title === missEnumCode && identity?.includes("maidenName") && (
              <FormGroup>
                <Row noGutters>
                  <Col>
                    <TextField
                      noFormGroup
                      name="person.maidenName"
                      label={loc("Maiden name")}
                      hasServerErrors={null}
                      initialValue={null}
                      trimOnBlur={true}
                    >
                      <InformationButton overlayText={loc("If you are not married, enter your name again.")} />
                    </TextField>
                  </Col>
                </Row>
              </FormGroup>
            )}
            {!isFirstNameBeforeLastName && firstNameField}
            <TextField
              hidden={!identity?.includes("taxNumber")}
              name="identity.taxNumber"
              label={loc`Tax number`}
              trimOnBlur={true}
            />
            <Date
              hidden={!identity?.includes("identificationExpirationDate")}
              name="identity.identificationExpirationDate"
              label={loc`Identification expiration date`}
              format={DATE_FORMAT.identity.identificationExpirationDate[country]}
            />
            <Date
              hidden={!identity?.includes("birthDate")}
              name="identity.birthDate"
              label={loc`Birthdate`}
              format={DATE_FORMAT.identity.birthDate[country]}
              disabled={values.identity?.disableFiscalCodeFields}
            />
            {tenant === TENANTS.SAMSUNG_IT && (
              <SelectIdentificationClientPage tunnelData={tunnelData} values={values} country={country} />
            )}
            {tenant !== TENANTS.SAMSUNG_IT && (
              <>
                <Select
                  hidden={!identity?.includes("birthCountry")}
                  name="identity.birthCountry"
                  label={loc`Country of birth`}
                  placeholder={loc`Select...`}
                  url={`${
                    tunnelData.apiUrl
                  }middle-business-service/api/v2/repository/countries/codes/${country}?languageTag=${encodeURIComponent(
                    languageTag
                  )}`}
                  token={tunnelData.access_token}
                  tenant={tenant}
                  disabled={values?.identity?.disableFiscalCodeFields}
                />
                <Select
                  hidden={!identity?.includes("birthDepartment")}
                  name="identity.birthDepartment"
                  placeholder={loc`Select...`}
                  label={loc`Department of birth`}
                  url={
                    values.identity?.birthCountry &&
                    `${
                      tunnelData.apiUrl
                    }middle-business-service/api/v2/repository/departments/codes?journeyCountryCode=${country}&countryCode=${
                      values.identity.birthCountry.value || values.identity.birthCountry
                    }`
                  }
                  token={tunnelData.access_token}
                  disabled={values?.identity?.disableFiscalCodeFields}
                />
                <Select
                  hidden={!identity?.includes("birthCity")}
                  name="identity.birthCity"
                  label={loc`City of birth`}
                  placeholder={loc`Select...`}
                  url={
                    values.identity?.birthDepartment &&
                    `${tunnelData.apiUrl}middle-business-service/api/v2/repository/cities/?departmentId=${
                      values.identity.birthDepartment.value
                    }&languageTag=${encodeURIComponent(languageTag)}`
                  }
                  token={tunnelData.access_token}
                  enableMatchLabel={true}
                  disabled={values?.identity?.disableFiscalCodeFields}
                />
              </>
            )}
            <TextField hidden={!coordinates?.includes("email")} name="coordinates.email" label={loc`Email`} />
            {isStrangerTenant(tenant) ? (
              <NumberFieldPhone
                countryCode={getCountryFromLanguage(tunnelData.data.order.orderLanguageTag)}
                hidden={!coordinates?.includes("phoneNumber")}
                name="coordinates.phoneNumber"
                label={loc`Mobile`}
                trimOnBlur={true}
                disabled={
                  tunnelData.data.order.simplifiedJourneyEnabled &&
                  !tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.phoneNumber?.editable
                }
              />
            ) : (
              <PhoneField
                hidden={!coordinates?.includes("phoneNumber")}
                name="coordinates.phoneNumber"
                label={loc`Mobile`}
                disabled={
                  tunnelData.data.order.simplifiedJourneyEnabled &&
                  !tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.phoneNumber?.editable
                }
              />
            )}
          </div>
        </Col>
      </Row>
      {(tunnelData.tenant !== TENANTS.MORTAR &&
        tunnelData.tenant !== TENANTS.BUT &&
        tunnelData.tenant !== TENANTS.CONFORAMA_RETAIL) ||
      !isLoadingOptins ||
      !tunnelData?.hideIdentificationPageOptins ? (
        <Row className="row-center mr-0 ml-0">
          <Optins optins={optins} tunnelData={tunnelData} showWeAskConsentTextBeforeOptins={country === "IT"} />
        </Row>
      ) : null}
      <Row className={clsx("footer-fixed", (isPdiRetailTenant(tenant) || tenant === "retail_bpls") && "row-center")}>
        {(isPdiRetailTenant(tenant) || tenant === "retail_bpls") && (
          <>
            <>
              {tenant !== TENANTS.BUT && !tunnelData?.isJourneyResumedAtHome && (
                <Col md={{ span: 3 }}>
                  <Button
                    {...(shopUrl && !useSimulationBackInsteadOfShop
                      ? { href: shopUrl }
                      : { onClick: backToSimulationPage })}
                    variant="outline-primary"
                    className="ico-back-small-before"
                  >
                    {loc("Back to shop")}
                  </Button>
                </Col>
              )}

              {!tunnelData.data.order.customer &&
                tenant !== TENANTS.MEDIAMARKT_NL &&
                tenant !== TENANTS.MEDIAMARKT_BE &&
                tenant !== TENANTS.CONFORAMA_RETAIL &&
                tenant !== TENANTS.BUT && (
                  <Col md={{ span: 3 }}>
                    <Button
                      id="simulation-save-button"
                      variant="success"
                      onClick={() => {
                        tunnelData.saveSimulation = true
                        onSubmit(values)
                      }}
                    >
                      {loc("identification_button_saveForLater")}
                    </Button>
                  </Col>
                )}
            </>

            {(tenant !== TENANTS.RETAIL_FNAC_PT &&
              tenant !== TENANTS.MEDIAMARKT_NL &&
              tenant !== TENANTS.MEDIAMARKT_BE) ||
            (tenant === TENANTS.RETAIL_FNAC_PT && tunnelData?.isJourneyResumedAtHome) ||
            tenant === TENANTS.MEDIAMARKT_NL ||
            tenant === TENANTS.MEDIAMARKT_BE ? (
              <Col md={{ span: 3 }}>
                <Button id="validate-button" variant="success" type="submit">
                  {loc`Validate`}
                </Button>
              </Col>
            ) : null}
          </>
        )}
        {!isPdiRetailTenant(tenant) && tenant !== "retail_bpls" && (
          <>
            <Col md={{ span: 3, offset: 1 }}>
              <Button href={tunnelData.shopUrl} variant="outline-primary" className="ico-back-small-before">
                {loc("Back to shop")}
              </Button>
            </Col>
            <Col md={{ span: 3, offset: 4 }}>
              <Button id="validate-button" variant="success" type="submit">
                {loc("Validate")}
              </Button>
            </Col>
          </>
        )}
      </Row>
    </Form>
  )
}
